import { Select } from '@chakra-ui/react';
import React from 'react';

import { usePremiumContext } from 'Contexts/premiumContext';
import { useTextContext } from 'Contexts/textContext';
import { NumberWithPayPeriod, Paragraph, Tooltip } from 'DesignLibrary/atoms';
import { PremiumPerDeduction } from 'DesignLibrary/atoms/PremiumPerDeduction';
import Plan from 'Models/plan';
import { PlanFit } from 'Shared/PlanFit';
import RichText from 'Shared/RichText';
import { SupplementalIcon } from 'Shared/SupplementalIcon';
import Text from 'Shared/Text';
import { createHraTooltip, createHsaTooltip, createSihraTooltip } from 'Utils/createTooltips';
import { toDollars } from 'Utils/helpers';

import { NetworkTier } from '../types';
import { EligibilitySticker, EmployerContribution } from './styled';
import { CompareTableRow } from '../_shared/CompareTableRow';

export interface MainDetailsProps {
  plans: Plan[];
  hsaContributionLimit: number;
  maximumAvailableAnnualSihraContribution: number;
  killMedRec: boolean;
  hsaBalance?: number;
  showOonInPbw: boolean;
  isNetworkTierToggleEnabled: boolean;
  getSelectedTier: (plan: Plan) => NetworkTier;
  setTierForPlan: (planId: string, tier: NetworkTier) => void;
}

export const MainDetails = ({
  plans,
  hsaContributionLimit,
  maximumAvailableAnnualSihraContribution,
  killMedRec,
  hsaBalance,
  showOonInPbw,
  isNetworkTierToggleEnabled,
  getSelectedTier,
  setTierForPlan,
}: MainDetailsProps) => {
  const { retrieveContentfulData } = useTextContext();
  const { payPeriodText } = usePremiumContext();

  const firstPlan = plans[0] || null;
  const secondPlan = plans[1] || null;
  const thirdPlan = plans[2] || null;

  const score = retrieveContentfulData<string>(
    'health_section.compare_plan_text.compareModal.terms.score.title',
  );
  const accountEligibility = retrieveContentfulData<string>('tool_tips.account_eligibility.title');
  const estimatedYearlyTitle = retrieveContentfulData<string>(
    'health_section.compare_plan_text.compareModal.terms.estYearlyTotal.title',
  );
  const premiumTitle = <Text field="health_section.compare_plan_text.compareModal.terms.premium.title" />;
  const oopTitle = retrieveContentfulData<string>(
    'health_section.compare_plan_text.compareModal.terms.oopEstimate.title',
  );

  // Line Item Title Tooltips
  const scoreTooltip = retrieveContentfulData<string>(
    'health_section.compare_plan_text.compareModal.terms.score.term',
  );
  const accountEligibilityTooltip = retrieveContentfulData<string>('tool_tips.account_eligibility.text');
  const networkTooltip =
    retrieveContentfulData<string>('tool_tips.network.text') ||
    'A group of doctors and hospitals covered by the plan. Your costs will generally be lower at a provider in the network, or most-preferred network, if there is one';
  const networkLabel =
    retrieveContentfulData<string>('health_section.compare_plan_text.compareModal.networkLabel') || 'Network';
  const inNetworkLabel =
    retrieveContentfulData<string>('health_section.compare_plan_text.compareModal.inNetwork') || 'In-Network';
  const outOfNetworkLabel =
    retrieveContentfulData<string>('health_section.compare_plan_text.compareModal.outOfNetwork') ||
    'Out-of-Network';
  const preferredNetworkLabel =
    retrieveContentfulData<string>('health_section.compare_plan_text.compareModal.preferredNetwork') ||
    'Most Preferred';

  const estimatedYearlyTooltip = (
    <>
      <Text field="tool_tips.estimated_yearly_total.text.p1" vars={{ x: payPeriodText }} />
      <Text field="tool_tips.estimated_yearly_total.text.p2" />
    </>
  );

  const premiumTooltip = <Text field="tool_tips.premium.text" vars={{ x: payPeriodText }} />;

  const oopTooltip = <Text field="tool_tips.out_of_pocket_estimate.text" />;

  // ACCOUNT ELIGIBILITY TAGS
  const renderHsaTag = (plan: Plan) => {
    if (plan.hsaEligible) {
      const employerHsaContribution = plan.calcEmployerHsaContribution(hsaContributionLimit) || 0;

      return (
        <>
          <EligibilitySticker className="hsa-eligible-sticker">
            <SupplementalIcon type="hsa" size="small" />
            <Paragraph size="small" weight="bold" color="--text-black">
              <Text field="health_section.eligibility_text.hsa.tag" />
            </Paragraph>
          </EligibilitySticker>
          {employerHsaContribution > 0 && (
            <EmployerContribution>
              <Paragraph data-testid="employer-hsa-contribution-text" color="--text-black" size="mini">
                <RichText
                  inlineParagraphs
                  toolTips={[
                    createHsaTooltip(
                      plan.isEmployerMatching,
                      plan.calcEmployerHsaContribution(hsaContributionLimit),
                      plan.employerHsaContributionLimit,
                      plan.employerHsaPercentageMatch,
                    ),
                  ]}
                  field="health_section.hsa_hra_employer_contribution"
                />
              </Paragraph>
            </EmployerContribution>
          )}
        </>
      );
    }
    return null;
  };

  const renderHraTag = (plan: Plan) => {
    if (plan.hraEligible) {
      const employerHraContribution = plan.employerHraContribution || 0;

      return (
        <>
          <EligibilitySticker className="hsa-eligible-sticker">
            <SupplementalIcon type="hra" size="small" />
            <Paragraph size="small" weight="bold" color="--text-black">
              <Text field="health_section.eligibility_text.hra.tag" />
            </Paragraph>
          </EligibilitySticker>
          {employerHraContribution > 0 && (
            <EmployerContribution>
              <Paragraph
                color="--text-black"
                size="mini"
                data-testid="employer-hra-contribution-text"
                className="employer-hra-contribution"
              >
                <RichText
                  inlineParagraphs
                  toolTips={[createHraTooltip(plan.employerHraContribution)]}
                  field="health_section.hsa_hra_employer_contribution"
                />
              </Paragraph>
            </EmployerContribution>
          )}
        </>
      );
    }
    return null;
  };

  const renderSihraTag = (plan: Plan) => {
    const employerSihraContribution = plan.annualSihraDollarsUsed || 0;

    if (employerSihraContribution) {
      return (
        <>
          <EligibilitySticker className="hsa-eligible-sticker">
            <SupplementalIcon type="hra" size="small" />
            <Paragraph size="small" weight="bold" color="--text-black">
              <Text field="health_section.eligibility_text.sihra.tag" />
            </Paragraph>
          </EligibilitySticker>
          <EmployerContribution>
            <Paragraph color="--text-black" size="mini" className="employer-sihra-contribution">
              <RichText
                inlineParagraphs
                toolTips={[createSihraTooltip(maximumAvailableAnnualSihraContribution)]}
                field="health_section.hsa_hra_employer_contribution"
              />
            </Paragraph>
          </EmployerContribution>
        </>
      );
    }
    return null;
  };

  const renderEligibilityStatus = (plan: Plan) => {
    const hsaTag = renderHsaTag(plan);
    const hraTag = renderHraTag(plan);
    const sihraTag = renderSihraTag(plan);

    if (!hsaTag && !hraTag && !sihraTag) {
      return null;
    }

    return (
      <>
        {hsaTag}
        {hraTag}
        {sihraTag}
      </>
    );
  };

  const renderNetworkTierDropdown = (plan: Plan) => {
    const hasMostPreferred = plan.hasMostPreferredNetwork;
    const isIndemnityPlan = plan.hasCombinedNetworkDeductible();
    // Show dropdown if FF is on AND plan has a preferred tier OR client has OON enabled (but not for indemnity plans)
    const showDropdown =
      isNetworkTierToggleEnabled && (hasMostPreferred || (showOonInPbw && !isIndemnityPlan));
    if (!showDropdown) return null;

    const selectedTier = getSelectedTier(plan);

    const options: { value: NetworkTier; label: string }[] = [];
    if (hasMostPreferred) {
      // Use builder-configured custom tier name if set, else fall back to default label
      const mpLabel =
        plan.useCustomTierName && plan.customTierName ? plan.customTierName : preferredNetworkLabel;
      options.push({ value: 'most_preferred', label: mpLabel });
    }
    options.push({ value: 'in_network', label: inNetworkLabel });
    if (showOonInPbw) {
      options.push({ value: 'out_of_network', label: outOfNetworkLabel });
    }

    return (
      <Select
        value={selectedTier}
        onChange={(e) => setTierForPlan(plan.planId, e.target.value as NetworkTier)}
        aria-label="Select network tier"
        borderRadius="8px"
        border="2px solid"
        borderColor="#2F2F52"
        bg="white"
        h="48px"
        w="175px"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>
    );
  };

  const renderPicwellScore = (plan: Plan) => {
    return <PlanFit variant="tag" score={plan.picwellScore} />;
  };

  const getCalculationTier = (plan: Plan): NetworkTier =>
    isNetworkTierToggleEnabled && plan.hasMostPreferredNetwork ? 'most_preferred' : 'in_network';

  const renderYearlyExpenseTotal = (plan: Plan) => {
    if (getSelectedTier(plan) !== getCalculationTier(plan)) {
      return <Paragraph size="small">N/A</Paragraph>;
    }

    const estimatedYearlyTotal = plan.realCostBeforeTaxBenefit;

    return (
      <NumberWithPayPeriod
        keyHint={`plan-${plan.planId}-yearly-total-value`}
        payPeriodType="yearly"
        value={estimatedYearlyTotal}
      />
    );
  };

  const renderPremium = (plan: Plan) => (
    <PremiumPerDeduction
      annualPremium={plan.annualPremium}
      deductions={plan.annualPremiumDeductions}
      currency
      keyHint={plan.planId}
    />
  );

  const renderOopEstimate = (plan: Plan) => {
    if (getSelectedTier(plan) !== getCalculationTier(plan)) {
      return <Paragraph size="small">N/A</Paragraph>;
    }

    const calculatedOutOfPocket = plan.calcOutOfPocket();

    const oopMax = plan.inNetworkOopMax || (plan.inNetworkMedOopMax || 0) + (plan.inNetworkDrugOopMax || 0);

    const isEstimateGreaterThanMax = calculatedOutOfPocket > oopMax;

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <NumberWithPayPeriod
            value={calculatedOutOfPocket}
            payPeriodType="yearly"
            keyHint={`plan-${plan.planId}-oop-est-value`}
          />
          {isEstimateGreaterThanMax && (
            <Tooltip
              label={{
                title: <Text field="tool_tips.yearly_estimate_greater_than_out_of_pocket_limit.title" />,
                text: <Text field="tool_tips.yearly_estimate_greater_than_out_of_pocket_limit.text" />,
              }}
            />
          )}
        </div>
        {hsaBalance !== undefined && hsaBalance > 0 && calculatedOutOfPocket > 0 && (
          <Paragraph size="mini" color="--text-gray" data-testid="hsa-balance-compare">
            <RichText
              inlineParagraphs
              field="health_section.recommendation.hsa_balance"
              vars={[toDollars(hsaBalance)]}
            />
          </Paragraph>
        )}
      </div>
    );
  };

  return (
    <>
      {/* PLAN SCORE */}
      {!killMedRec && (
        <CompareTableRow
          rowHeader={score}
          rowHeaderTooltip={{ text: scoreTooltip }}
          columnOne={firstPlan && renderPicwellScore(firstPlan)}
          columnTwo={secondPlan && renderPicwellScore(secondPlan)}
          columnThree={thirdPlan && renderPicwellScore(thirdPlan)}
          keyHint="plan-score"
        />
      )}

      {/* NETWORK TIER */}
      {isNetworkTierToggleEnabled && (
        <CompareTableRow
          rowHeader={networkLabel}
          rowHeaderTooltip={networkTooltip}
          columnOne={firstPlan && renderNetworkTierDropdown(firstPlan)}
          columnTwo={secondPlan && renderNetworkTierDropdown(secondPlan)}
          columnThree={thirdPlan && renderNetworkTierDropdown(thirdPlan)}
          keyHint="network-tier"
        />
      )}

      {/* ACCOUNT ELIGIBILITY */}
      <CompareTableRow
        rowHeader={accountEligibility}
        rowHeaderTooltip={{ text: accountEligibilityTooltip }}
        columnOne={firstPlan && renderEligibilityStatus(firstPlan)}
        columnTwo={secondPlan && renderEligibilityStatus(secondPlan)}
        columnThree={thirdPlan && renderEligibilityStatus(thirdPlan)}
        size="medium"
        keyHint="account-eligibility"
      />

      {/* YEARLY EXPENSE TOTAL */}
      <CompareTableRow
        rowHeader={estimatedYearlyTitle}
        rowHeaderTooltip={{ text: estimatedYearlyTooltip }}
        columnOne={firstPlan && renderYearlyExpenseTotal(firstPlan)}
        columnTwo={secondPlan && renderYearlyExpenseTotal(secondPlan)}
        columnThree={thirdPlan && renderYearlyExpenseTotal(thirdPlan)}
        keyHint="yearly-expense-total"
      />

      {/* PREMIUM */}
      <CompareTableRow
        rowHeader={premiumTitle}
        rowHeaderTooltip={{ text: premiumTooltip }}
        columnOne={firstPlan && renderPremium(firstPlan)}
        columnTwo={secondPlan && renderPremium(secondPlan)}
        columnThree={thirdPlan && renderPremium(thirdPlan)}
        keyHint="premium"
      />

      {/* OOP ESTIMATE */}
      <CompareTableRow
        rowHeader={oopTitle}
        rowHeaderTooltip={{ text: oopTooltip }}
        columnOne={firstPlan && renderOopEstimate(firstPlan)}
        columnTwo={secondPlan && renderOopEstimate(secondPlan)}
        columnThree={thirdPlan && renderOopEstimate(thirdPlan)}
        keyHint="oop-estimate"
      />
    </>
  );
};
